import { Modal, TitleBar } from '@shopify/app-bridge-react';
import { AppProvider, Box } from '@shopify/polaris';
import type { SpaceScale } from '@shopify/polaris-tokens';
import { ResponsiveProp } from '@shopify/polaris/build/ts/src/utilities/css';
import en from '@shopify/polaris/locales/en.json';
import { PostMessageType } from 'hooks/post-message/modal/post-message-type';
import { usePostMessage } from 'hooks/post-message/usePostMessage';
import React, { createContext, useCallback, useState } from 'react';

interface ModalContextState {
    open: boolean;
    setOpen: (isOpened: boolean) => void;
    setContent: (content: ModalContent) => void;
}

export interface ModalContent {
    title: string;
    component: React.ReactNode | React.JSX.Element;
    variant?: 'small' | 'base' | 'large' | 'max';
    padding?: ResponsiveProp<SpaceScale>;
    onClose?: VoidFunction;
    closeButtonLabel?: string;
    primaryAction?: { btnLabel: string; action: () => void };
}

const defaultModalContext: ModalContextState = {
    open: false,
    setOpen: () => {},
    setContent: () => {},
};

const ModalContext = createContext<ModalContextState>(defaultModalContext);

interface ModalContextProviderProps {
    children: React.ReactNode;
}

export const ModalContextProvider: React.FC<ModalContextProviderProps> = ({ children }) => {
    const [open, setOpen] = useState<boolean>(false);
    const [content, setContent] = useState<ModalContent>({ title: '', component: undefined });
    const handleChange = useCallback((): void => {
        content?.onClose?.();
        setContent({ title: '', component: undefined });
        setOpen(false);
    }, [content, open]);

    usePostMessage<{ title: string }>(PostMessageType.SET_MODAL_TITLE, (data) => {
        setContent((prev) => ({ ...prev, title: data.title }));
    });

    return (
        <ModalContext.Provider
            value={{
                open,
                setOpen: setOpen,
                setContent: setContent,
            }}
        >
            {children}
            {!!content?.component && (
                <Modal open={open} onHide={handleChange} id={'shared-modal'} variant={content.variant}>
                    <AppProvider i18n={en}>
                        <Box padding={content.padding ?? '400'}>
                            <React.Fragment>{content.component}</React.Fragment>
                        </Box>
                    </AppProvider>
                    <TitleBar title={content.title}>
                        {content.primaryAction && (
                            // eslint-disable-next-line react/no-unknown-property
                            <button variant="primary" onClick={content.primaryAction.action}>
                                {content.primaryAction.btnLabel}
                            </button>
                        )}
                        {content.closeButtonLabel && <button onClick={handleChange}>{content.closeButtonLabel}</button>}
                    </TitleBar>
                </Modal>
            )}
        </ModalContext.Provider>
    );
};

export default ModalContext;
